import { Box } from "@outschool/backpack";
import { ActivityEnrollQueryQuery } from "@outschool/gql-frontend-generated";
import React, { useMemo } from "react";

import * as Section from "../../../shared/Section";
import { PayUpfront, PayWeekly } from "./enrollmentForm/RecurringSelection";
import { useOrderContext } from "./OrderContext";

export default function UpfrontOrWeeklyCreditsSelection({
  activity,
  section
}: {
  activity: Pick<
    NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["activity"],
    | "weekly_meetings"
    | "allows_recurring_payment"
    | "is_ongoing_weekly"
    | "isClub"
    | "duration_weeks"
    | "pricingInfo"
    | "price_cents"
    | "isSelfPaced"
    | "isIncludedWithMembership"
  >;
  section?: Pick<
    NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["section"],
    "progress" | "isPublished" | "price_cents" | "allowsLateEnrollment"
  > | null;
}) {
  const { isWeeklyPayment, setIsWeeklyPayment, pricingBreakdown } =
    useOrderContext();

  const isLastWeekOfFixedLengthActivity =
    !!section?.progress &&
    !!activity?.weekly_meetings &&
    section.progress.remainingMeetingCount <= activity.weekly_meetings;

  const isWeeklyPaymentEligible = useMemo(() => {
    return (
      activity.allows_recurring_payment &&
      !activity.is_ongoing_weekly &&
      !activity.isClub &&
      !isLastWeekOfFixedLengthActivity &&
      !!activity.duration_weeks &&
      activity.duration_weeks > 1
    );
  }, [
    activity.allows_recurring_payment,
    activity.is_ongoing_weekly,
    activity.isClub,
    isLastWeekOfFixedLengthActivity,
    activity.duration_weeks
  ]);

  if (
    !isWeeklyPaymentEligible ||
    !pricingBreakdown ||
    !activity.duration_weeks
  ) {
    return null;
  }

  const upfrontCredits = pricingBreakdown.perEnrollmentUpfrontCredits as number;
  const weeklyCredits = pricingBreakdown.perEnrollmentWeeklyCredits as number;

  const isBuyoutSection = !!section && Section.isBuyoutSection(section);

  const shouldShowUpfrontDiscount =
    !activity.isIncludedWithMembership &&
    !isBuyoutSection &&
    !!activity.pricingInfo?.upfrontDiscountPercentage;

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", marginBottom: "20px" }}
    >
      <PayUpfront
        activity={activity}
        section={section}
        isWeeklyPayment={isWeeklyPayment}
        setIsWeeklyPayment={setIsWeeklyPayment}
        shouldShowUpfrontDiscount={shouldShowUpfrontDiscount}
        upfrontPrice={upfrontCredits}
        weeklyTotalPrice={pricingBreakdown.perEnrollmentTotalWeeklyCredits!}
        upfrontTotalPrice={pricingBreakdown.perEnrollmentUpfrontCredits!}
        showPriceInCredits={true}
      />
      {!activity.isIncludedWithMembership && (
        <PayWeekly
          activity={activity}
          isWeeklyPayment={isWeeklyPayment}
          setIsWeeklyPayment={setIsWeeklyPayment}
          weeklyPrice={weeklyCredits}
          shouldShowUpfrontDiscount={shouldShowUpfrontDiscount}
          showPriceInCredits={true}
        />
      )}
    </Box>
  );
}
